import React from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Grid, IconButton, CircularProgress, Button, Box } from '@material-ui/core'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import Image from '@v4/components/common/FAIcons/Solid/Image'
import TrashAlt from '@v4/components/common/FAIcons/Light/TrashAlt'
import ImagePolaroid from '@v4/components/common/FAIcons/Light/ImagePolaroid'
import EditableHTML from '@v4/components/common/EditableHTML'
import { EDITOR_VARIANT } from '@v4/hooks/useEditor'
import { SortableElement } from 'react-sortable-hoc'
import { useOptionItem } from './OptionItem.hooks'
import DragHandle from '../../../../components/DragHandle'

const OptionItem = props => {
	const [
		{
			classes,
			loading,
			disabled,
			draggable,
			label,
			imagePath,
			editable,
			deletable,
			withLabels,
			showCheckBox,
			showGalleryIcon,
			gridDesktopSize,
			placeholder,
			buttonRef,
		},
		{
			handleLabelEdit,
			handleDelete,
			handleClickGalleryIcon,
			handleLabelFocus,
			handleClick,
			handleKeyUp,
			handleKeyDown,
		},
	] = useOptionItem(props)

	return (
		<Grid
			item
			xs={6}
			sm={6}
			md={gridDesktopSize}
			lg={gridDesktopSize}
			className={clsx(classes.root, 'q-image-item-root')}
		>
			<Button
				variant="elevation"
				ref={buttonRef}
				className={clsx(classes.button, showCheckBox && classes.checked, 'q-image-item-button')}
				classes={{ label: classes.labelWrapper }}
				onClick={handleClick}
				disabled={disabled}
				onKeyUp={handleKeyUp}
				onKeyDown={handleKeyDown}
			>
				<div className={clsx(classes.media, 'q-image-item-media')}>
					{draggable && <DragHandle className={classes.dragButton} />}

					{(showGalleryIcon || deletable) && (
						<div className={clsx(classes.actionIcons)}>
							{showGalleryIcon && (
								<IconButton
									component="span"
									className={clsx(classes.actionButton, classes.galleryButton)}
									onClick={handleClickGalleryIcon}
								>
									<Image className={classes.galleryIcon} />
								</IconButton>
							)}
							{deletable && (
								<IconButton
									component="span"
									className={clsx(classes.actionButton, classes.deleteButton)}
									onClick={handleDelete}
								>
									<TrashAlt className={classes.deleteIcon} />
								</IconButton>
							)}
						</div>
					)}

					<div className={clsx(classes.mediaContainer, 'q-image-item-media-container')}>
						<Box className={classes.imageWrap}>
							{imagePath ? (
								<img src={imagePath} alt="" className={clsx(classes.image, 'q-image-item-img')} />
							) : (
								<ImagePolaroid className={classes.polaroidIcon} />
							)}
						</Box>

						{loading && <CircularProgress size={24} className={classes.progress} />}
						{showCheckBox && <CheckBoxIcon className={classes.checkBox} />}
					</div>
				</div>

				{withLabels && (
					<div className={clsx(classes.content, 'q-image-item-label-content')}>
						{editable ? (
							<EditableHTML
								key="editable"
								className="q-image-item-label"
								html={label}
								component="div"
								variant="inherit"
								displayEmpty
								editable
								editorVariant={EDITOR_VARIANT.EMPHASIS}
								editorValidElements="br,i/em,b/strong,u,span[style]"
								onChange={handleLabelEdit}
								onFocus={handleLabelFocus}
								placeholder={placeholder}
							/>
						) : (
							<EditableHTML
								key="readonly"
								html={label}
								className="q-image-item-label"
								component="div"
								variant="inherit"
							/>
						)}
					</div>
				)}
			</Button>
		</Grid>
	)
}

OptionItem.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	index: PropTypes.number,
	label: PropTypes.node,
	asset: PropTypes.object,
	showLabels: PropTypes.bool,
	editable: PropTypes.bool,
	checked: PropTypes.bool,
	deletable: PropTypes.bool,
	draggable: PropTypes.bool,
	loading: PropTypes.bool,
	pending: PropTypes.bool,
	onLabelEdit: PropTypes.func,
	onDelete: PropTypes.func,
	onClick: PropTypes.func,
	dragId: PropTypes.string,
	answersPerRow: PropTypes.number,
	autoFocus: PropTypes.bool,
	placeholder: PropTypes.string,
	invertColors: PropTypes.bool,
}

export default SortableElement(OptionItem)
